import { useState } from "react"

import { useNavigate } from "react-router-dom"

import type { ChatSlice } from "@/stores/slices/chat-slice"
import type { StoreApi, UseBoundStore } from "zustand"

import { useRoutes } from "@/contexts/use-routes"
import { useM365Enabled, useWebSearchEnabled } from "@/hooks/use-route-overrides"
import { ChatService } from "@/services/chat-service"
import { useAIExplorerStore } from "@/stores/ai-explorer-store"
import { useNavigatorStore } from "@/stores/navigator-store"
import { type SessionType } from "@/types"

type UseStartNewChat = {
  useChatStore: () => UseBoundStore<StoreApi<ChatSlice>>
  sessionType: SessionType
}

export function useStartNewChat({ useChatStore, sessionType }: UseStartNewChat) {
  const [chatService] = useState<ChatService>(ChatService.getInstance())
  const navigate = useNavigate()
  const { getRoutePath, getRouteById } = useRoutes()

  const chatStore = useChatStore()
  const resetFiles = chatStore((state) => state.resetFiles)
  const resetDocuments = chatStore((state) => state.resetDocuments)
  const resetChatState = chatStore((state) => state.resetChatState)
  const setCurrentSessionId = chatStore((state) => state.setCurrentSessionId)

  const explorerStore = useAIExplorerStore(sessionType)
  const setEnableWebSearch = explorerStore((state) => state.setEnableWebSearch)
  const setReasoningMode = explorerStore((state) => state.setReasoningMode)
  const setModelOverride = explorerStore((state) => state.setModelOverride)
  const setEffortOverride = explorerStore((state) => state.setEffortOverride)
  const setEnableM365 = explorerStore((state) => state.setEnableM365)
  const setEnableDeep = explorerStore((state) => state.setEnableDeep)
  const clearResponseTiming = explorerStore((state) => state.clearResponseTiming)

  const isWebSearchAvailable = useWebSearchEnabled()
  const isM365Available = useM365Enabled()

  const navigatorStore = useNavigatorStore(sessionType)

  function resetToNewChat({ replace = false }: { replace?: boolean } = {}) {
    // Clear current session state before dropping currentSessionId.
    // resetChatState uses currentSessionId internally.
    resetChatState()
    chatService.closeSession(sessionType)
    setCurrentSessionId(null)
    resetFiles()
    resetDocuments()

    // Reset Explorer-specific settings if this is a free chat component
    const route = getRouteById(sessionType)
    if (route?.component === "free") {
      setEnableWebSearch(isWebSearchAvailable)
      setEnableM365(isM365Available)
      // Deep is a per-chat choice: a new chat never inherits it (#3060).
      setEnableDeep(false)
      setReasoningMode("auto")
      setModelOverride(null)
      setEffortOverride(null)
      clearResponseTiming()
    }

    navigatorStore.getState().setActiveCitations([])
    navigatorStore.getState().setCitations([])
    // Deep Explorer state is per-session (lives on `sessionStates[sid]`),
    // so dropping `currentSessionId` here naturally hands the user a clean
    // slate. The prior session's slot stays put — important when the user
    // backgrounded a run and we still want its frames to flow into its own
    // slot rather than the new one.
    void navigate(getRoutePath(sessionType), replace ? { replace: true } : undefined)
  }

  function handleStartNewChat() {
    resetToNewChat()
  }

  function handleSharedLeaveRedirect() {
    resetToNewChat({ replace: true })
  }

  return { handleStartNewChat, handleSharedLeaveRedirect }
}
